import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Award, Sparkles, Settings, Plus, Pencil, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoyaltyPage = () => {
  const [settings, setSettings] = useState(null);
  const [bonusEvents, setBonusEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: '', multiplier: 2, start_date: '', end_date: '', active: true
  });

  useEffect(() => {
    Promise.all([fetchSettings(), fetchEvents()]).finally(() => setLoading(false));
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/loyalty/settings`);
      setSettings(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/loyalty/bonus-events`);
      setBonusEvents(res.data);
    } catch (err) { console.error(err); }
  };

  const saveSettings = async () => {
    try {
      await axios.post(`${API}/loyalty/settings`, settings);
      toast.success('Loyalty settings saved');
    } catch (err) { toast.error('Failed to save'); }
  };

  const openAddEvent = () => {
    setEditingEvent(null);
    setEventForm({ name: '', multiplier: 2, start_date: '', end_date: '', active: true });
    setShowEventModal(true);
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name,
      multiplier: event.multiplier,
      start_date: event.start_date,
      end_date: event.end_date,
      active: event.active
    });
    setShowEventModal(true);
  };

  const saveEvent = async () => {
    if (!eventForm.name || !eventForm.start_date || !eventForm.end_date) {
      toast.error('All fields required'); return;
    }
    try {
      if (editingEvent) {
        await axios.put(`${API}/loyalty/bonus-events/${editingEvent.id}`, eventForm);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/loyalty/bonus-events`, eventForm);
        toast.success('Event created');
      }
      setShowEventModal(false);
      fetchEvents();
    } catch (err) { toast.error('Failed'); }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Delete this bonus event?')) return;
    try {
      await axios.delete(`${API}/loyalty/bonus-events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (err) { toast.error('Failed'); }
  };

  const toggleEventActive = async (event) => {
    try {
      await axios.put(`${API}/loyalty/bonus-events/${event.id}`, { active: !event.active });
      fetchEvents();
    } catch (err) { toast.error('Failed'); }
  };

  if (loading || !settings) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  }

  return (
    <div data-testid="loyalty-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Loyalty Programme</h1>
        <p className="text-slate-500 mt-1 font-body">Configure points, redemption, and bonus events</p>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Settings className="w-5 h-5" /> Programme Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base">Loyalty Programme Enabled</Label>
            <Switch
              data-testid="loyalty-enabled-toggle"
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings(s => ({...s, enabled: v}))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Points per Pound Spent</Label>
              <Input data-testid="points-per-pound" type="number" min="1"
                value={settings.points_per_pound}
                onChange={(e) => setSettings(s => ({...s, points_per_pound: parseInt(e.target.value) || 1}))} />
            </div>
            <div>
              <Label>Points Redemption Rate (pts = £1)</Label>
              <Input data-testid="redemption-rate" type="number" min="1"
                value={settings.redemption_rate}
                onChange={(e) => setSettings(s => ({...s, redemption_rate: parseInt(e.target.value) || 100}))} />
            </div>
            <div>
              <Label>Meal Deal Points Required</Label>
              <Input data-testid="meal-deal-points" type="number" min="1"
                value={settings.meal_deal_points}
                onChange={(e) => setSettings(s => ({...s, meal_deal_points: parseInt(e.target.value) || 500}))} />
            </div>
            <div>
              <Label>Meal Deal Value (£)</Label>
              <Input data-testid="meal-deal-value" type="number" step="0.01" min="0"
                value={settings.meal_deal_value}
                onChange={(e) => setSettings(s => ({...s, meal_deal_value: parseFloat(e.target.value) || 0}))} />
            </div>
            <div>
              <Label>Redemption Cap per Transaction (optional)</Label>
              <Input data-testid="redemption-cap" type="number" min="0"
                placeholder="No limit"
                value={settings.redemption_cap_per_transaction || ''}
                onChange={(e) => setSettings(s => ({...s, redemption_cap_per_transaction: e.target.value ? parseInt(e.target.value) : null}))} />
            </div>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
            <strong>Current rates:</strong> {settings.points_per_pound} point per £1 spent.
            {settings.redemption_rate} points = £1 off. {settings.meal_deal_points} points = free meal deal (£{settings.meal_deal_value?.toFixed(2)}).
            Points expire 31st December each year.
          </div>

          <Button data-testid="save-loyalty-settings" onClick={saveSettings}>Save Settings</Button>
        </CardContent>
      </Card>

      {/* Bonus Events */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5" /> Bonus Point Events
          </CardTitle>
          <Button size="sm" data-testid="add-bonus-event-btn" onClick={openAddEvent}>
            <Plus className="w-4 h-4 mr-1" /> New Event
          </Button>
        </CardHeader>
        <CardContent>
          {bonusEvents.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No bonus events. Create one to boost engagement!</p>
          ) : (
            <div className="space-y-3">
              {bonusEvents.map((ev) => {
                const now = new Date().toISOString().slice(0, 10);
                const isLive = ev.active && ev.start_date <= now && ev.end_date >= now;
                return (
                  <div key={ev.id} data-testid={`bonus-event-${ev.id}`}
                    className={`flex items-center justify-between p-4 rounded-lg border ${isLive ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{ev.name}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                          {ev.multiplier}x points
                        </span>
                        {isLive && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">LIVE</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        {new Date(ev.start_date).toLocaleDateString('en-GB')} — {new Date(ev.end_date).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={ev.active} onCheckedChange={() => toggleEventActive(ev)} />
                      <Button size="sm" variant="ghost" onClick={() => openEditEvent(ev)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteEvent(ev.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit' : 'New'} Bonus Event</DialogTitle>
            <DialogDescription>Configure a bonus point multiplier for a date range</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Event Name</Label>
              <Input data-testid="event-name" placeholder="e.g. Double Points Tuesday"
                value={eventForm.name} onChange={(e) => setEventForm(f => ({...f, name: e.target.value}))} />
            </div>
            <div>
              <Label>Points Multiplier</Label>
              <Input data-testid="event-multiplier" type="number" step="0.5" min="1"
                value={eventForm.multiplier} onChange={(e) => setEventForm(f => ({...f, multiplier: parseFloat(e.target.value) || 2}))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input data-testid="event-start" type="date"
                  value={eventForm.start_date} onChange={(e) => setEventForm(f => ({...f, start_date: e.target.value}))} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input data-testid="event-end" type="date"
                  value={eventForm.end_date} onChange={(e) => setEventForm(f => ({...f, end_date: e.target.value}))} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={eventForm.active} onCheckedChange={(v) => setEventForm(f => ({...f, active: v}))} />
              <Label>Active</Label>
            </div>
            <Button data-testid="save-event-btn" className="w-full" onClick={saveEvent}>
              {editingEvent ? 'Update' : 'Create'} Event
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoyaltyPage;
